/**
 * Employee Management Page
 * Requirements: 14.8-14.9 - Employee management
 */
import { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography, Alert, CircularProgress, FormControl, InputLabel,
  Select, MenuItem, IconButton, TextField, Checkbox, FormControlLabel,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { organizationApi } from '../api/organizationApi';
import { userApi } from '../api/userApi';
import type { Company, Department, Employee } from '../types/organization.types';
import type { CreateUserDTO, UserRole } from '../types/user.types';
import { PermissionLevel } from '../types/permission.types';

export const EmployeeManagementPage = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // User registration dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState<CreateUserDTO>({
    email: '',
    username: '',
    name: '',
    jobTitle: '',
    role: 'user' as UserRole,
    permissionLevel: PermissionLevel.SELF_ONLY,
    companyId: '',
    departmentId: '',
    isPrimaryDepartment: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadDepartments(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (selectedDeptId) {
      loadEmployees(selectedDeptId);
    }
  }, [selectedDeptId]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const data = await organizationApi.getCompanies();
      setCompanies(data);
      if (data.length > 0 && !selectedCompanyId) {
        setSelectedCompanyId(data[0].id);
      }
    } catch (err) {
      setError('会社一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async (companyId: string) => {
    try {
      const data = await organizationApi.getDepartmentsByCompany(companyId);
      setDepartments(data);
      if (data.length > 0) {
        setSelectedDeptId(data[0].id);
      }
    } catch (err) {
      setError('部門一覧の取得に失敗しました');
    }
  };

  const loadEmployees = async (deptId: string) => {
    try {
      setLoading(true);
      const data = await organizationApi.getEmployeesByDepartment(deptId);
      setEmployees(data);
    } catch (err) {
      setError('従業員一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveEmployee = async (userId: string) => {
    if (!selectedDeptId) return;
    try {
      await organizationApi.removeEmployeeFromDepartment(selectedDeptId, userId);
      setSuccess('従業員を部門から削除しました');
      await loadEmployees(selectedDeptId);
    } catch (err) {
      setError('従業員の削除に失敗しました');
    }
  };

  const handleOpenCreateDialog = () => {
    setNewUser({
      email: '',
      username: '',
      name: '',
      jobTitle: '',
      role: 'user' as UserRole,
      permissionLevel: PermissionLevel.SELF_ONLY,
      companyId: selectedCompanyId || '',
      departmentId: selectedDeptId || '',
      isPrimaryDepartment: true,
    });
    setGeneratedPassword('');
    setShowPassword(false);
    setCreateDialogOpen(true);
  };

  const handleCreateUser = async () => {
    try {
      const result = await userApi.createUser(newUser);
      setGeneratedPassword(result.temporaryPassword || '');
      setShowPassword(true);
      setSuccess(`ユーザーを登録しました: ${result.email}`);

      // Reload employees if we're viewing the department we just assigned to
      if (selectedDeptId && newUser.departmentId === selectedDeptId) {
        await loadEmployees(selectedDeptId);
      }
    } catch (err) {
      setError('ユーザー登録に失敗しました');
      setShowPassword(false);
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setGeneratedPassword('');
    setShowPassword(false);
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>従業員管理</Typography>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Box display="flex" gap={2} mb={3} alignItems="center">
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>会社</InputLabel>
          <Select value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)} label="会社">
            {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>部門</InputLabel>
          <Select value={selectedDeptId} onChange={(e) => setSelectedDeptId(e.target.value)} label="部門" disabled={!selectedCompanyId}>
            {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          disabled={!selectedCompanyId}
        >
          新規ユーザー登録
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>名前</TableCell>
                    <TableCell>メールアドレス</TableCell>
                    <TableCell>役職</TableCell>
                    <TableCell>権限レベル</TableCell>
                    <TableCell>部門</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center">従業員が登録されていません</TableCell></TableRow>
                  ) : (
                    employees.map((emp) => (
                      <TableRow key={emp.id}>
                        <TableCell>{emp.name}</TableCell>
                        <TableCell>{emp.email}</TableCell>
                        <TableCell>{emp.jobTitle || '-'}</TableCell>
                        <TableCell>{emp.permissionLevel}</TableCell>
                        <TableCell>
                          {emp.departments && emp.departments.length > 0 ? (
                            emp.departments.map((d) => (
                              <Chip key={d.id} label={d.name} size="small" color={d.isPrimary ? 'primary' : 'default'} sx={{ mr: 0.5 }} />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => handleRemoveEmployee(emp.id)} color="error" size="small"><DeleteIcon /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>新規ユーザー登録</DialogTitle>
        <DialogContent>
          {showPassword && generatedPassword ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                ユーザーが正常に登録されました。以下の仮パスワードをユーザーに伝えてください。
              </Alert>
              <TextField
                fullWidth
                label="仮パスワード"
                value={generatedPassword}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary">
                ユーザーは初回ログイン時にパスワードの変更を求められます。
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="ユーザー名"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="メールアドレス"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="名前"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="役職"
                value={newUser.jobTitle}
                onChange={(e) => setNewUser({ ...newUser, jobTitle: e.target.value })}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>ロール</InputLabel>
                <Select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as UserRole })}
                  label="ロール"
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>権限レベル</InputLabel>
                <Select
                  value={newUser.permissionLevel}
                  onChange={(e) => setNewUser({ ...newUser, permissionLevel: e.target.value as PermissionLevel })}
                  label="権限レベル"
                >
                  {Object.values(PermissionLevel).map((level) => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>会社</InputLabel>
                <Select
                  value={newUser.companyId}
                  onChange={(e) => setNewUser({ ...newUser, companyId: e.target.value })}
                  label="会社"
                  required
                >
                  {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>部門 (オプション)</InputLabel>
                <Select
                  value={newUser.departmentId || ''}
                  onChange={(e) => setNewUser({ ...newUser, departmentId: e.target.value || undefined })}
                  label="部門 (オプション)"
                >
                  <MenuItem value="">なし</MenuItem>
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {newUser.departmentId && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newUser.isPrimaryDepartment}
                      onChange={(e) => setNewUser({ ...newUser, isPrimaryDepartment: e.target.checked })}
                    />
                  }
                  label="主部門として設定"
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>
            {showPassword ? '閉じる' : 'キャンセル'}
          </Button>
          {!showPassword && (
            <Button
              onClick={handleCreateUser}
              variant="contained"
              disabled={!newUser.email || !newUser.username || !newUser.name || !newUser.companyId}
            >
              登録
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
